import React, { useMemo } from 'react';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { formatCurrency, formatNumber, MetricMatrix } from './SharedFrames';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function buildMonths(from, to, days) {
    const result = [];
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${to}T00:00:00`);

    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
        const y = cur.getFullYear();
        const m = cur.getMonth();
        const label = cur.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const monthDays = [];

        const daysInMonth = new Date(y, m + 1, 0).getDate();
        for (let d = 1; d <= daysInMonth; d += 1) {
            const dt = new Date(y, m, d);
            const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            if (dt < start || dt > end) continue;
            const dow = dt.getDay();
            const mondayBased = dow === 0 ? 6 : dow - 1;
            monthDays.push({ date: iso, dayOfMonth: d, dayOfWeek: mondayBased, day: days[iso] });
        }
        if (monthDays.length > 0) {
            result.push({ year: y, month: m, label, days: monthDays });
        }
        cur = new Date(y, m + 1, 1);
    }
    return result;
}

function cellKind(daySummary, dateStr) {
    const dt = new Date(`${dateStr}T00:00:00`);
    const dow = dt.getDay();
    if (dow === 0 || dow === 6) return 'weekend';
    if (!daySummary) return 'empty';
    const net = Number(daySummary.netPnl);
    if (Number.isFinite(net) && net > 0) return 'profit';
    if (Number.isFinite(net) && net < 0) return 'loss';
    if (daySummary.tradeCount > 0) return 'flat';
    return 'empty';
}

const CELL_STYLES = {
    profit: {
        light: { bg: 'rgba(16,185,129,0.22)', border: 'rgba(16,185,129,0.45)' },
        dark: { bg: 'rgba(16,185,129,0.28)', border: 'rgba(16,185,129,0.5)' },
    },
    loss: {
        light: { bg: 'rgba(239,68,68,0.22)', border: 'rgba(239,68,68,0.45)' },
        dark: { bg: 'rgba(239,68,68,0.28)', border: 'rgba(239,68,68,0.5)' },
    },
    flat: {
        light: { bg: 'rgba(148,163,184,0.18)', border: 'rgba(148,163,184,0.35)' },
        dark: { bg: 'rgba(148,163,184,0.22)', border: 'rgba(148,163,184,0.4)' },
    },
    empty: {
        light: { bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.15)' },
        dark: { bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.18)' },
    },
    weekend: {
        light: { bg: 'transparent', border: 'transparent' },
        dark: { bg: 'transparent', border: 'transparent' },
    },
};

function formatDayLabel(dateStr) {
    const dt = new Date(`${dateStr}T12:00:00`);
    return dt.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

function MonthGrid({ month, onClickDay, selectedDay }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.5,
                borderRadius: 2,
                background: (t) => (t.palette.mode === 'dark' ? 'rgba(30,41,59,0.4)' : 'rgba(241,245,249,0.6)'),
                border: '1px solid',
                borderColor: 'divider',
                minWidth: 230,
            }}
        >
            <Typography variant="caption" fontWeight={700} display="block" mb={0.5} textAlign="center">
                {month.label}
            </Typography>
            <Box display="grid" gridTemplateColumns="repeat(7, 30px)" gap="2px" justifyContent="center">
                {WEEKDAYS.map((d) => (
                    <Typography key={d} variant="caption" textAlign="center" color="text.secondary" fontSize={10}>
                        {d}
                    </Typography>
                ))}
                {month.days.length > 0
                    && Array.from({ length: month.days[0].dayOfWeek }).map((_, i) => (
                        <Box key={`blank-${i}`} />
                    ))}
                {month.days.map((d) => {
                    const kind = cellKind(d.day, d.date);
                    const clickable = kind === 'profit' || kind === 'loss' || kind === 'flat';
                    const isSelected = selectedDay === d.date;
                    const tooltip = d.day
                        ? `Net P&L on ${formatDayLabel(d.date)}: ${formatCurrency(d.day.netPnl)} (${d.day.tradeCount} trade${d.day.tradeCount === 1 ? '' : 's'})`
                        : `${formatDayLabel(d.date)} — no trades`;
                    return (
                        <Tooltip key={d.date} title={tooltip} arrow placement="top">
                            <Box
                                onClick={() => clickable && onClickDay?.(d.date)}
                                sx={(t) => ({
                                    width: 30,
                                    height: 30,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: clickable ? 'pointer' : 'default',
                                    background: CELL_STYLES[kind][t.palette.mode].bg,
                                    border: '1px solid',
                                    borderColor: isSelected
                                        ? t.palette.primary.main
                                        : CELL_STYLES[kind][t.palette.mode].border,
                                    boxShadow: isSelected ? `0 0 0 2px ${t.palette.primary.main}` : 'none',
                                    fontWeight: isSelected ? 700 : 400,
                                    transition: 'transform 0.15s, box-shadow 0.15s',
                                    '&:hover': clickable ? { transform: 'scale(1.12)', zIndex: 1 } : {},
                                })}
                            >
                                <Typography fontSize={11} lineHeight={1}>
                                    {d.dayOfMonth}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Paper>
    );
}

function Legend() {
    const items = [
        { label: 'Profit day', color: 'rgba(16,185,129,0.45)' },
        { label: 'Loss day', color: 'rgba(239,68,68,0.45)' },
        { label: 'Flat day', color: 'rgba(148,163,184,0.35)' },
        { label: 'No trades', color: 'rgba(148,163,184,0.15)' },
    ];
    return (
        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            {items.map((it) => (
                <Box key={it.label} display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 0.5, background: it.color }} />
                    <Typography variant="caption" color="text.secondary">
                        {it.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

const PnlCalendarHeatMap = ({
    from,
    to,
    days = {},
    totals = {},
    loading = false,
    error = null,
    onClickDay,
    selectedDay = null,
}) => {
    const months = useMemo(() => buildMonths(from, to, days), [from, to, days]);

    const winDayRate = useMemo(() => {
        const { winDays = 0, tradingDays = 0 } = totals;
        if (!tradingDays) return null;
        return ((winDays / tradingDays) * 100).toFixed(1);
    }, [totals]);

    return (
        <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    {from} to {to} (IST sessions from 05:00). Click a day to browse its trades below — the calendar stays on this range.
                </Typography>
                <Legend />
            </Box>

            {loading ? (
                <Typography variant="body2" color="text.secondary">Loading calendar…</Typography>
            ) : null}

            {error ? (
                <Typography variant="body2" color="error">
                    Could not load daily P&L: {error}
                </Typography>
            ) : null}

            <Box display="flex" flexWrap="wrap" gap={3}>
                {months.map((month) => (
                    <MonthGrid
                        key={`${month.year}-${month.month}`}
                        month={month}
                        onClickDay={onClickDay}
                        selectedDay={selectedDay}
                    />
                ))}
            </Box>

            <MetricMatrix
                columns={{ xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }}
                metrics={[
                    { label: 'Trading days', value: formatNumber(totals.tradingDays, 0) },
                    {
                        label: 'Net P&L',
                        value: formatCurrency(totals.netPnl),
                        tone: (totals.netPnl ?? 0) >= 0 ? 'success' : 'error',
                    },
                    { label: 'Win days', value: formatNumber(totals.winDays, 0) },
                    { label: 'Loss days', value: formatNumber(totals.lossDays, 0) },
                    { label: 'Win rate (days)', value: winDayRate ? `${winDayRate}%` : '—' },
                    { label: 'Round trips', value: formatNumber(totals.tradeCount, 0) },
                ]}
            />
        </Stack>
    );
};

export default PnlCalendarHeatMap;
